import type { ColumnDef } from "@tanstack/react-table";

import { HelperOptions } from "@/components/DataTable";
import { DataTableColumnHeader } from "@/components/DataTable/Columns/DataTableColumnHeader";
import { VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { type Preset, useFormatDate, useFormatDistance } from "@/hooks/date";
import { isDate } from "@/utils/typeGuards";

type NestedKeys<T> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? `${K}` | `${K}.${NestedKeys<T[K]>}`
        : never;
    }[keyof T]
  : never;

/** Date,DateTimeを表示するためのカラム */
export function dateAndDurationColumn<TData>(
  id: NestedKeys<TData> & string,
  preset: keyof Preset = "date",
  options: HelperOptions & {},
): ColumnDef<TData> {
  const {
    i18nKey = "createdOn",
    minWidth = "200px",
    sortable = false,
    enableHiding = true,
  } = options;
  return {
    accessorKey: id,
    meta: { i18nKey, thStyle: { minWidth } },
    enableSorting: sortable,
    enableHiding,
    header(props) {
      return <DataTableColumnHeader {...props} />;
    },
    cell: function Cell({ getValue }) {
      const formatDate = useFormatDate();
      const formatDistance = useFormatDistance();
      const value = getValue<string | Date | null>();

      // 日付値のバリデーション
      const date =
        value instanceof Date ? value : value ? new Date(value) : null;
      const isValidDate = isDate(date);

      return (
        <VStack>
          <Text className="text-sm text-neutral-on-fill">
            {isValidDate ? formatDate(date, { preset }) : "-"}
          </Text>
          <Text className="text-xs text-neutral-muted-on-fill">
            {isValidDate ? formatDistance(date, new Date()) : "-"}
          </Text>
        </VStack>
      );
    },
  };
}
